import { useMemo, useRef, useState } from "react"
import type { ColumnDef, PaginationState } from "@tanstack/react-table"
import { Trash2, TriangleAlert } from "lucide-react"

import { DataTable } from "@/components/DataTable"
import { EditUserDialog } from "@/components/subject/EditUserDialog"
import { Status } from "@/components/Status"
import { Button } from "@/components/ui/button"
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogMedia,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog"
import type { UserTableRow } from "@/components/subject/userTableData"
import type { OtkRole, Role } from "@/lib/api/users"
import { useReadOnly } from "@/store/session"

function DestructiveUserAction({
  disabled,
  onConfirm,
  row,
}: {
  disabled?: boolean
  /** May call a real backend endpoint (e.g. deleting a user) — the dialog
   *  stays open with an inline error on rejection instead of closing
   *  optimistically, mirroring `EditUserDialog.handleSave`. */
  onConfirm: () => void | Promise<void>
  row: UserTableRow
}) {
  const closeWithPointer = useRef(false)
  const [open, setOpen] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const label = "Видалити Користувача"
  const Icon = Trash2

  async function handleConfirm() {
    if (submitting) return
    setSubmitting(true)
    setError(null)
    try {
      await onConfirm()
      setOpen(false)
    } catch {
      setError("Не вдалося видалити користувача. Спробуйте ще раз.")
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <AlertDialog
      onOpenChange={(nextOpen) => {
        if (nextOpen) setError(null)
        setOpen(nextOpen)
      }}
      open={open}
    >
      <AlertDialogTrigger asChild>
        <Button
          disabled={disabled}
          size="sm"
          title={
            disabled
              ? "Суб'єкт виключений з реєстру — доступний лише перегляд"
              : undefined
          }
          variant="destructive"
        >
          <Icon data-icon="inline-start" />
          {label}
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent
        onCloseAutoFocus={(event) => {
          if (closeWithPointer.current) event.preventDefault()
        }}
        onKeyDownCapture={() => {
          closeWithPointer.current = false
        }}
        onPointerDownCapture={() => {
          closeWithPointer.current = true
        }}
      >
        <AlertDialogHeader>
          <AlertDialogMedia className="bg-destructive/10 text-destructive">
            <TriangleAlert />
          </AlertDialogMedia>
          <AlertDialogTitle>{label}</AlertDialogTitle>
          <AlertDialogDescription>
            {`Користувача ${row.email} буде видалено, і він втратить доступ до АРМ.`}
          </AlertDialogDescription>
        </AlertDialogHeader>
        {error && <p className="text-sm text-destructive">{error}</p>}
        <AlertDialogFooter>
          <AlertDialogCancel disabled={submitting}>Назад</AlertDialogCancel>
          <Button
            disabled={submitting}
            onClick={handleConfirm}
            variant="destructive"
          >
            <Icon data-icon="inline-start" />
            {submitting ? "Видалення…" : label}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}

export function UsersTable({
  canManageUsers,
  data,
  includeRole = true,
  isAdmin,
  onDeleteUser,
  onEditUser,
  roleRows = [],
}: {
  canManageUsers: boolean
  data: UserTableRow[]
  includeRole?: boolean
  isAdmin: boolean
  onDeleteUser?: (id: string) => void | Promise<void>
  onEditUser?: (id: string, roleId: Role) => void
  roleRows?: OtkRole[]
}) {
  const readOnly = useReadOnly()
  const hasAdminActions =
    isAdmin &&
    canManageUsers &&
    onDeleteUser !== undefined &&
    onEditUser !== undefined
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  })

  const columns = useMemo<ColumnDef<UserTableRow>[]>(
    () => [
      {
        accessorKey: "email",
        header: "E-mail",
        meta: { className: includeRole ? "w-64" : "w-64 pl-4" },
      },
      {
        accessorKey: "responsiblePerson",
        header: "Відповідальна особа",
        meta: { className: "min-w-64 whitespace-normal" },
      },
      ...(includeRole
        ? [
            {
              accessorKey: "role",
              header: "Роль в АРМ",
              meta: { className: "w-72 whitespace-normal break-words" },
            } satisfies ColumnDef<UserTableRow>,
          ]
        : []),
      {
        accessorKey: "status",
        header: "Статус",
        meta: { className: "w-44" },
        cell: ({ row }) => <Status>{row.original.status}</Status>,
      },
      {
        accessorKey: "note",
        header: "Примітки",
        meta: { className: "w-44 whitespace-normal break-words" },
        cell: ({ row }) => row.original.note,
      },
    ],
    [includeRole]
  )

  return (
    <DataTable
      columns={columns}
      data={data}
      getRowId={(row) => row.id}
      onPaginationChange={setPagination}
      pagination={pagination}
      rowActions={
        hasAdminActions
          ? (row) => (
              <>
                <EditUserDialog
                  disabled={readOnly}
                  onSave={(roleId) => onEditUser?.(row.original.id, roleId)}
                  roleRows={roleRows}
                  user={row.original}
                />
                <DestructiveUserAction
                  disabled={readOnly}
                  onConfirm={() => onDeleteUser?.(row.original.id)}
                  row={row.original}
                />
              </>
            )
          : undefined
      }
      tableClassName="min-w-[72rem] table-auto"
    />
  )
}
